import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Box, MenuItem, TextField, Button, Grid } from '@mui/material';
import { useQueryParams } from '../hooks/useQueryParam.js';
import { useDispatch, useSelector } from 'react-redux';
import { getGroups, getUserProvider } from '../../actions/auth.js';
import { availableFilters } from '../utils/availableFilters.js';
import { getActualSeason, getSeasonDates } from '../../bdd/bddSTUB.js';
import { Can } from '../../permissions/Can.js';
import { usePermissions } from '../../permissions/usePermissions.js';
import { PERMISSIONS } from '../../permissions/permissionKeys.js';

const Filters = ({ filtersConfig, onApplyFilters, setCSVFilters, onReset, className, setFiltersCallback }) => {
  const { getQueryParams, setQueryParams } = useQueryParams();
  const dispatch = useDispatch();

    const { getPermission } = usePermissions();
    const providerPermission = getPermission(PERMISSIONS.FILTER_PROVIDER);

  const [filters, setFilters] = useState(() => {
    const queryParams = getQueryParams();
    const initialFilters = {};

    filtersConfig.forEach(({ name,isDateCustomizable, fullAccess }) => {
      if (name === 'provider') {
        //todo refactor fullAccess not specifically used
        if (fullAccess?.allowed && providerPermission?.allowed) {dispatch(getGroups())};
        initialFilters[name] = queryParams.get(name) || getUserProvider();
      } else if (name === 'season') {
        initialFilters[name] = queryParams.get(name) || getActualSeason();
        if (isDateCustomizable) {
          const { startDate, endDate } = getSeasonDates(initialFilters[name]);

          initialFilters.startDate = startDate;
          initialFilters.endDate = endDate;
        }
      } else {
        initialFilters[name] = queryParams.get(name) || '';
      }
    });

    return initialFilters;
  });

  const [optionsData, setOptionsData] = useState({});
  const groups = useSelector((state) => state.authentication.groups);

  // useEffect(() => {
  //   dispatch(getGroups());
  // }, [dispatch]);

  useEffect(() => {
  if (typeof setCSVFilters === 'function') {
    // clone des ids
    const enriched = { ...filters };

    // helper pour retrouver le label d'une option (value -> label)
    const findLabel = (key, value) => {
      if (!value) return '';
      const filterConfig = availableFilters[key];
      const name = key; // chez toi key === name (provider, discipline, season, competition, teams)
      const options = filterConfig?.getOptions(optionsData[name] || []) || [];
      return options.find((o) => o.value === value)?.label || '';
    };

    // ajoute des champs lisibles pour le filename
    enriched.competitionName = findLabel('competition', filters.competition);
    enriched.teamName = findLabel('teams', filters.teams);

    // (optionnel) providerName si tu veux (attention: parfois c'est déjà "dsas")
    enriched.providerName = findLabel('provider', filters.provider);

    setCSVFilters(enriched);
  }

  if (typeof setFiltersCallback === 'function') {
    setFiltersCallback(filters);
  }
}, [filters, setCSVFilters, setFiltersCallback, optionsData]);

  useEffect(() => {
    filtersConfig.forEach(async ({ key, name }) => {
      const filterConfig = availableFilters[key];

      const dependenciesSatisfied = filterConfig.dependencies.every((dep) => filters[dep]);
      if (!dependenciesSatisfied) return;

      if (filterConfig.fetchFunction) {
        const { data } = await filterConfig.fetchFunction(filters);
        setOptionsData((prev) => ({ ...prev, [name]: data }));
      }
    });

    setOptionsData((prev) => ({ ...prev, provider: groups }));
  }, [filters, availableFilters, groups]);

  useEffect(() => {
    const seasonFilter = filtersConfig.find((f) => f.name === 'season');
    if (seasonFilter?.isDateCustomizable && filters.season) {
      const { startDate, endDate } = getSeasonDates(filters.season);
      setFilters((prevFilters) => ({
        ...prevFilters,
        startDate: prevFilters.startDate || startDate,
        endDate: prevFilters.endDate || endDate,
      }));
    }
  }, [filters.season]);

  const handleFilterChange = useCallback((e) => {
    const { name, value } = e.target;

    setFilters((prevFilters) => {
      const changedIndex = filtersConfig.findIndex(f => f.key === name);

      const newFilters = { ...prevFilters, [name]: value };

      filtersConfig.forEach((filter, index) => {
        if (index > changedIndex && filter.key !== 'season') {
          newFilters[filter.key] = "";
        }
      });
      const { startDate, endDate } = getSeasonDates(prevFilters['season']);

      newFilters.startDate = startDate;
      newFilters.endDate = endDate;

      return newFilters;
    });
  }, []);

  const handleDateChange = (e) => {
    const { name, value } = e.target;
  
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value ? new Date(value + 'T00:00:00Z') : null, // Convertir YYYY-MM-DD en Date
    }));
  };

  const applyFilters = useCallback(() => {
    const { startDate, endDate, ...filtersWithoutDates } = filters;
    setQueryParams(filtersWithoutDates);

    const isMissingFilter = Object.keys(filters).find(k => !filters[k]);
    if (isMissingFilter) {
      onReset();
      return;
    }

    onApplyFilters(filters);
  }, [filters]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  return (
    
    <Grid container spacing={1.5} sx={{alignItems : 'flex-end'}}>
           
      {filters && filtersConfig.map(({ key, name, isDateCustomizable }) => {

        const filterConfig = availableFilters[key];
        const options = filterConfig.getOptions(optionsData[name] || []);

        return (
          <React.Fragment key={name}>
            <>
            <Can permission={filterConfig.permissionKey}>
              <Grid item xs={12} sm={12} md>
                  <TextField
                    className={className}
                    fullWidth
                    key={name}
                    select
                    label={filterConfig.label}
                    name={name}
                    error={(name==="discipline" && !filters['discipline']) || (name==="competition" && !filters['competition']) || (name==="teams" && !filters['teams'])}
                    value={filters[name] || ''}
                    onChange={handleFilterChange}
                    disabled={filterConfig.disabled(filters)}
                  >
                    {options.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
            </Can>

            {isDateCustomizable && name === 'season' && (
              <>
                <Grid item xs={6} sm={6} md>
                  <TextField
                    className={className}
                    fullWidth
                    type="date"
                    label="Date de début"
                    name="startDate"
                    value={filters.startDate ? filters.startDate.toISOString().split('T')[0] : ''}
                    onChange={handleDateChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6} sm={6} md>
                  <TextField
                    className={className}
                    fullWidth
                    type="date"
                    label="Date de fin"
                    name="endDate"
                    value={filters.endDate ? filters.endDate.toISOString().split('T')[0] : ''}
                    onChange={handleDateChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </>
            )}  
            </>
          </React.Fragment>
        );
      })}
    </Grid>
  );
};

export default Filters;
